import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { NgOptimizedImage } from '@angular/common';

type AvatarSize = 'sm' | 'md' | 'lg' | 'xl';

const SIZE_MAP: Record<AvatarSize, number> = {
  sm: 32,
  md: 40,
  lg: 56,
  xl: 96,
};

@Component({
  selector: 'app-user-avatar',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgOptimizedImage],
  host: {
    '[class]': 'sizeClass()',
    '[style.width.px]': 'sizePx()',
    '[style.height.px]': 'sizePx()',
  },
  template: `
    @if (src()) {
      <img
        [ngSrc]="src()!"
        [width]="sizePx()"
        [height]="sizePx()"
        [alt]="alt()"
        class="avatar-image" />
    } @else {
      <span class="avatar-initials" [attr.aria-label]="alt()">
        {{ initials() }}
      </span>
    }
  `,
  styles: `
    :host {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      background-color: oklch(0.9 0.05 250);
      overflow: hidden;
      flex-shrink: 0;
    }

    .avatar-image {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .avatar-initials {
      font-weight: 600;
      color: oklch(0.4 0.1 250);
      text-transform: uppercase;
    }

    :host.sm .avatar-initials { font-size: 0.75rem; }
    :host.md .avatar-initials { font-size: 0.875rem; }
    :host.lg .avatar-initials { font-size: 1.25rem; }
    :host.xl .avatar-initials { font-size: 2rem; }
  `,
})
export class UserAvatarComponent {
  readonly src = input<string | null>();
  readonly name = input.required<string>();
  readonly size = input<AvatarSize>('md');

  protected readonly sizePx = computed(() => SIZE_MAP[this.size()]);
  protected readonly sizeClass = computed(() => this.size());

  protected readonly alt = computed(() => `Avatar for ${this.name()}`);

  protected readonly initials = computed(() => {
    const name = this.name();
    if (!name) return '?';

    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) {
      return parts[0].substring(0, 2);
    }
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  });
}
